import { useMemo, useState } from 'react'
import { Calendar, ChevronDown, ChevronUp, Users } from 'lucide-react'
import type { CustomDataArtifact } from '@dev-streetbeat/chat/types'

import { Button } from '@/components/ui/button'
import styles from './MarketCalendarArtifact.module.scss'

export const MARKET_EVENTS_SCREEN_EVENT = 'credem:market-events-screen'
export interface MarketEventsScreenDetail { prompt: string }

// chat-ui's ArtifactProps type isn't re-exported from the package root, so
// redeclare the minimal runtime contract (mirrors ClientListArtifact).
interface ArtifactProps<T> {
  artifact: T
  onLoaded?: () => void
}

// ---- Payload types (locked contract — credem.market_calendar v1) ----

interface MacroFiltersApplied {
  impact?: string[]
  countries?: string[]
}

interface MacroCountsFilteredOut {
  [label: string]: number
}

interface MacroItem {
  datetime_rome: string
  tz_label?: string | null
  country: string
  event: string
  impact: string
  currency?: string | null
  previous?: string | null
  estimate?: string | null
  actual?: string | null
  change?: number | null
  changePercentage?: number | null
}

interface EarningsCoverage {
  resolved: number
  total_equity: number
  unresolved_isins?: string[]
}

interface EarningsItem {
  report_date: string
  date_confidence?: string | null
  name: string
  isin?: string | null
  symbol?: string | null
  listing_note?: string | null
  clients_count?: number | null
}

type SectionStatus = 'events' | 'empty_no_events' | 'stale_served' | 'error'

interface MacroSection {
  category: 'macro'
  title: string
  status: SectionStatus
  as_of?: string | null
  filters_applied?: MacroFiltersApplied | null
  counts_filtered_out?: MacroCountsFilteredOut | null
  items: MacroItem[]
}

interface EarningsSection {
  category: 'earnings'
  title: string
  scope?: string
  status: SectionStatus
  as_of?: string | null
  coverage?: EarningsCoverage | null
  items: EarningsItem[]
}

type CalendarSection = MacroSection | EarningsSection

interface MarketCalendarWindow {
  from?: string
  to?: string
  resolved_from_clock?: string
}

interface MarketCalendarPayload {
  window?: MarketCalendarWindow | null
  sections: CalendarSection[]
  source_summary?: string | null
}

// ---- Payload parsing ----

function coerceString(v: unknown): string {
  return typeof v === 'string' ? v : ''
}

function coerceNullableString(v: unknown): string | null {
  if (typeof v === 'string') return v
  return null
}

function coerceNullableNumber(v: unknown): number | null {
  if (typeof v === 'number') return v
  return null
}

function coerceNullableStringOrNumber(v: unknown): string | null {
  if (typeof v === 'string') return v
  if (typeof v === 'number') return String(v)
  return null
}

function parseMacroItem(raw: Record<string, unknown>): MacroItem {
  return {
    datetime_rome: coerceString(raw.datetime_rome),
    tz_label: coerceNullableString(raw.tz_label),
    country: coerceString(raw.country),
    event: coerceString(raw.event),
    impact: coerceString(raw.impact),
    currency: coerceNullableString(raw.currency),
    previous: coerceNullableStringOrNumber(raw.previous),
    estimate: coerceNullableStringOrNumber(raw.estimate),
    // Never fabricate actual — only set when explicitly non-null in the payload
    actual: coerceNullableStringOrNumber(raw.actual),
    change: coerceNullableNumber(raw.change),
    changePercentage: coerceNullableNumber(raw.changePercentage),
  }
}

function parseEarningsItem(raw: Record<string, unknown>): EarningsItem {
  return {
    report_date: coerceString(raw.report_date),
    date_confidence: coerceNullableString(raw.date_confidence),
    name: coerceString(raw.name),
    isin: coerceNullableString(raw.isin),
    symbol: coerceNullableString(raw.symbol),
    listing_note: coerceNullableString(raw.listing_note),
    clients_count: coerceNullableNumber(raw.clients_count),
  }
}

function parseSection(raw: Record<string, unknown>): CalendarSection | null {
  const category = raw.category
  if (category !== 'macro' && category !== 'earnings') return null

  const title = coerceString(raw.title)
  const statusRaw = raw.status
  const status: SectionStatus =
    statusRaw === 'events' ||
    statusRaw === 'empty_no_events' ||
    statusRaw === 'stale_served' ||
    statusRaw === 'error'
      ? statusRaw
      : 'events'
  const as_of = coerceNullableString(raw.as_of)

  const itemsRaw = Array.isArray(raw.items) ? raw.items : []

  if (category === 'macro') {
    const items: MacroItem[] = []
    for (const item of itemsRaw) {
      if (item && typeof item === 'object') {
        items.push(parseMacroItem(item as Record<string, unknown>))
      }
    }

    const filtersRaw = raw.filters_applied
    const filters_applied: MacroFiltersApplied | null =
      filtersRaw && typeof filtersRaw === 'object' ? (filtersRaw as MacroFiltersApplied) : null

    const countsRaw = raw.counts_filtered_out
    const counts_filtered_out: MacroCountsFilteredOut | null =
      countsRaw && typeof countsRaw === 'object'
        ? (countsRaw as MacroCountsFilteredOut)
        : null

    return { category: 'macro', title, status, as_of, filters_applied, counts_filtered_out, items }
  } else {
    // earnings
    const items: EarningsItem[] = []
    for (const item of itemsRaw) {
      if (item && typeof item === 'object') {
        items.push(parseEarningsItem(item as Record<string, unknown>))
      }
    }

    const coverageRaw = raw.coverage
    let coverage: EarningsCoverage | null = null
    if (coverageRaw && typeof coverageRaw === 'object') {
      const c = coverageRaw as Record<string, unknown>
      coverage = {
        resolved: typeof c.resolved === 'number' ? c.resolved : 0,
        total_equity: typeof c.total_equity === 'number' ? c.total_equity : 0,
        unresolved_isins: Array.isArray(c.unresolved_isins)
          ? c.unresolved_isins.filter((x): x is string => typeof x === 'string')
          : [],
      }
    }

    const scope = coerceString(raw.scope)

    return { category: 'earnings', title, scope, status, as_of, coverage, items }
  }
}

export function parsePayload(data: unknown): MarketCalendarPayload | null {
  if (!data || typeof data !== 'object') return null
  const raw = data as Record<string, unknown>

  const sectionsRaw = Array.isArray(raw.sections) ? raw.sections : []
  const sections: CalendarSection[] = []

  for (const sectionRaw of sectionsRaw) {
    if (!sectionRaw || typeof sectionRaw !== 'object') continue
    const parsed = parseSection(sectionRaw as Record<string, unknown>)
    if (!parsed) continue

    // Drop sections whose category is not macro/earnings (already done in parseSection).
    // Also drop sections whose items are empty AND status is "events" — nothing to render.
    if (parsed.status === 'events' && parsed.items.length === 0) continue

    sections.push(parsed)
  }

  const windowRaw = raw.window
  let win: MarketCalendarWindow | null = null
  if (windowRaw && typeof windowRaw === 'object') {
    const w = windowRaw as Record<string, unknown>
    win = {
      from: coerceString(w.from),
      to: coerceString(w.to),
      resolved_from_clock: coerceString(w.resolved_from_clock),
    }
  }

  return {
    window: win,
    sections,
    source_summary: coerceNullableString(raw.source_summary),
  }
}

// ---- Agenda types and helpers ----

const INITIAL_VISIBLE = 6

type AgendaRow =
  | { kind: 'macro'; sortKey: Date; item: MacroItem }
  | { kind: 'earnings'; sortKey: Date; item: EarningsItem }

// Parses "YYYY-MM-DD HH:MM" into a Date. Falls back to epoch on bad input.
function parseMacroDate(s: string): Date {
  // "2026-07-02 14:30" → parts[0]="2026-07-02", parts[1]="14:30"
  const parts = s.split(' ')
  if (parts.length === 2) {
    const d = new Date(`${parts[0]}T${parts[1]}:00`)
    if (!isNaN(d.getTime())) return d
  }
  const fallbackDate = new Date(s)
  return isNaN(fallbackDate.getTime()) ? new Date(0) : fallbackDate
}

// Parses "YYYY-MM-DD" into a Date at 00:00.
function parseEarningsDate(s: string): Date {
  const d = new Date(`${s}T00:00:00`)
  return isNaN(d.getTime()) ? new Date(0) : d
}

function buildAgenda(sections: CalendarSection[]): AgendaRow[] {
  const rows: AgendaRow[] = []
  for (const section of sections) {
    if (section.category === 'macro') {
      for (const item of section.items) {
        rows.push({ kind: 'macro', sortKey: parseMacroDate(item.datetime_rome), item })
      }
    } else {
      for (const item of section.items) {
        rows.push({ kind: 'earnings', sortKey: parseEarningsDate(item.report_date), item })
      }
    }
  }
  // Stable sort ascending by sortKey
  rows.sort((a, b) => a.sortKey.getTime() - b.sortKey.getTime())
  return rows
}

// Formats a Date as "DD/MM"
function formatDayMonth(d: Date): string {
  const day = String(d.getDate()).padStart(2, '0')
  const month = String(d.getMonth() + 1).padStart(2, '0')
  return `${day}/${month}`
}

// Formats a Date as "HH:MM"
function formatTime(d: Date): string {
  const h = String(d.getHours()).padStart(2, '0')
  const m = String(d.getMinutes()).padStart(2, '0')
  return `${h}:${m}`
}

// ---- Single agenda row renderer ----

function AgendaRowView({ row }: { row: AgendaRow }) {
  if (row.kind === 'macro') {
    const { item, sortKey } = row
    const hasEstimate = item.estimate != null
    const hasPrevious = item.previous != null
    const hasMeta = hasEstimate || hasPrevious

    return (
      <li className={styles.agendaRow}>
        <div className={styles.agendaLeft}>
          <span className={styles.agendaDate}>{formatDayMonth(sortKey)}</span>
          <span className={styles.agendaTime}>
            {formatTime(sortKey)} · Roma
          </span>
        </div>
        <div className={styles.agendaMiddle}>
          <span className={`${styles.agendaChip} ${styles.agendaChipMacro}`}>
            Macro · {item.country}
          </span>
          <p className={styles.agendaHeadline}>{item.event}</p>
        </div>
        {hasMeta ? (
          <div className={styles.agendaRight}>
            {item.actual != null ? (
              <span className={styles.agendaMeta}>
                <span className={styles.agendaMetaLabel}>att. </span>
                {item.actual}
              </span>
            ) : hasEstimate ? (
              <span className={styles.agendaMeta}>
                <span className={styles.agendaMetaLabel}>att. </span>
                {item.estimate}
              </span>
            ) : null}
            {hasPrevious ? (
              <span className={styles.agendaMeta}>
                <span className={styles.agendaMetaLabel}>prec. </span>
                {item.previous}
              </span>
            ) : null}
          </div>
        ) : null}
      </li>
    )
  }

  // earnings
  const { item, sortKey } = row
  const hasClients = item.clients_count != null && item.clients_count > 0
  const clientiLabel = hasClients
    ? item.clients_count === 1
      ? '1 cliente'
      : `${item.clients_count} clienti`
    : null

  const screeningPrompt = `Quali clienti del mio portafoglio detengono ${item.name}${item.symbol ? ` (${item.symbol})` : ''}?`

  return (
    <li className={styles.agendaRow}>
      <div className={styles.agendaLeft}>
        <span className={styles.agendaDate}>{formatDayMonth(sortKey)}</span>
        {item.date_confidence ? (
          <span className={styles.agendaTime}>{item.date_confidence}</span>
        ) : null}
      </div>
      <div className={styles.agendaMiddle}>
        <span className={`${styles.agendaChip} ${styles.agendaChipEarnings}`}>
          Trimestrale
        </span>
        <p className={styles.agendaHeadline}>
          {item.name}
          {item.symbol ? (
            <>
              {' · '}
              <code className={styles.agendaSymbol}>{item.symbol}</code>
            </>
          ) : null}
        </p>
      </div>
      {clientiLabel ? (
        <div className={styles.agendaRight}>
          <Button
            variant="outline"
            size="xs"
            aria-label={`Trova i clienti che detengono ${item.name}`}
            title="Trova i clienti che detengono questo titolo"
            onClick={(event) => {
              event.currentTarget.dispatchEvent(
                new CustomEvent<MarketEventsScreenDetail>(MARKET_EVENTS_SCREEN_EVENT, {
                  bubbles: true,
                  composed: true,
                  detail: { prompt: screeningPrompt },
                }),
              )
            }}
          >
            <Users aria-hidden />
            {clientiLabel}
          </Button>
        </div>
      ) : null}
    </li>
  )
}

// ---- Main component ----

/**
 * Renderer for the credem.market_calendar v1 custom-data artifact emitted by
 * the agent's advisor_market_events_get MCP tool.
 *
 * Renders macro economic events and upcoming earnings as a single merged
 * agenda list sorted chronologically. First 6 rows are visible; the rest
 * are revealed via a collapse toggle.
 *
 * - Null payload → renders nothing.
 * - Sections with empty items + status="events" are skipped.
 * - Unknown section categories are dropped.
 * - impact is never rendered.
 * - actual/change fields are only rendered when actual is explicitly non-null.
 */
export function MarketCalendarArtifact({ artifact }: ArtifactProps<CustomDataArtifact>) {
  const payload = useMemo(() => parsePayload(artifact.custom_data?.data), [artifact])
  const [expanded, setExpanded] = useState(false)

  if (!payload) return null

  const agenda = buildAgenda(payload.sections)

  // Collect per-section statuses for notices
  const staleSection = payload.sections.find((s) => s.status === 'stale_served')
  const errorSection = payload.sections.find((s) => s.status === 'error')

  // Find earnings coverage (from the first earnings section that has it)
  const earningsSection = payload.sections.find(
    (s): s is EarningsSection => s.category === 'earnings',
  ) as EarningsSection | undefined
  const coverage = earningsSection?.coverage ?? null

  const total = agenda.length
  const hasMore = total > INITIAL_VISIBLE
  const visible = expanded || !hasMore ? agenda : agenda.slice(0, INITIAL_VISIBLE)
  const hiddenCount = total - INITIAL_VISIBLE

  const hasNotices = Boolean(staleSection || errorSection)
  const isEmpty = agenda.length === 0 && !hasNotices

  return (
    <section className={styles.card} aria-label="Calendario eventi di mercato">
      <header className={styles.header}>
        <div className={styles.titleBlock}>
          <Calendar className={styles.titleIcon} aria-hidden />
          <h4 className={styles.title}>Calendario eventi di mercato</h4>
        </div>
      </header>

      {staleSection ? (
        <p className={styles.statusNotice} role="status">
          Dati aggiornati al {staleSection.as_of || '—'}, possibili variazioni.
        </p>
      ) : null}

      {errorSection ? (
        <p className={`${styles.statusNotice} ${styles.statusNoticeError}`} role="alert">
          Dati non disponibili al momento.
        </p>
      ) : null}

      {isEmpty ? (
        <p className={styles.empty}>Nessun evento in calendario nella finestra richiesta.</p>
      ) : null}

      {agenda.length > 0 ? (
        <ul className={styles.agendaList} role="list">
          {visible.map((row, idx) => (
            <AgendaRowView key={idx} row={row} />
          ))}
        </ul>
      ) : null}

      {hasMore ? (
        <button
          type="button"
          onClick={() => setExpanded((current) => !current)}
          className={styles.expander}
          aria-expanded={expanded}
        >
          {expanded ? (
            <>
              Mostra meno <ChevronUp className={styles.expanderIcon} aria-hidden />
            </>
          ) : (
            <>
              Mostra altri {hiddenCount} <ChevronDown className={styles.expanderIcon} aria-hidden />
            </>
          )}
        </button>
      ) : null}

      {coverage || payload.source_summary ? (
        <footer className={styles.footer}>
          {coverage ? (
            <p className={styles.coverageLine}>
              Copertura trimestrali: {coverage.resolved}/{coverage.total_equity} risolti — titoli
              detenuti alla fotografia più recente; date in calendario dal vero oggi
            </p>
          ) : null}
          {payload.source_summary ? (
            <span className={styles.sourceSummary}>{payload.source_summary}</span>
          ) : null}
        </footer>
      ) : null}
    </section>
  )
}
